import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { getChartTitleTranslation } from "../../translations/translations";
import { Tree, TreeEventNodeEvent } from "primereact/tree";
import { ContextMenu } from "primereact/contextmenu";
import { Menu } from "primereact/menu";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Well, Wellbore, Log, TreeNode } from "../../store/types";
import {
  TreeContainer,
  TreeBody,
  TreeSearchBar,
  TreeControls,
  StyledContextMenu,
  NodeContainer,
} from "./TreeView.styles";

interface TreeViewProps {
  wells: Well[];
  wellbores: Wellbore[] | undefined;
  logs: Log[] | undefined;
  isDataLoading?: boolean;
  fetchWellbores: (wellUid: string) => void;
  fetchLogs: (wellUid: string, wellboreUid: string) => void;
  onAddWell?: (name: string) => void;
  onAddWellbore?: (wellUid: string, name: string) => void;
  onAddLog?: (wellUid: string, wellboreUid: string, name: string) => void;
  onEditNode?: (node: TreeNode, newName: string) => void;
  onDeleteNode?: (node: TreeNode) => void;
  onDeleteLog?: (log: Log) => Promise<{ success: boolean; error?: string }>;
  onViewWellbore?: (wellbore: Wellbore) => void;
  onToggleObserve?: (
    wellUid: string,
    wellboreUid: string,
    logUid: string
  ) => void;
  isObserved?: (logUid: string) => boolean;
  isLogActive?: (log: Log) => boolean;
  selectedWellbore?: Wellbore | null;
  treeControls?: boolean;
  isContextMenu?: boolean;
}

type TreeSortMode = "name" | "dateTimeLastChange";

const normalizeSearchText = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLocaleLowerCase("ru-RU");

const getNodeLastChangeValue = (node: TreeNode): Date | string | undefined => {
  if (node.type === "log") {
    return node.data?.commonData?.dTimLastChange;
  }

  return node.data?.dateTimeLastChange ?? node.data?.commonData?.dTimLastChange;
};

const getNodeLastChangeTimestamp = (node: TreeNode) => {
  const value = getNodeLastChangeValue(node);
  if (!value) return null;

  const timestamp = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const formatLastChangeTooltip = (node: TreeNode) => {
  const value = getNodeLastChangeValue(node);
  if (!value) return undefined;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return `Последнее изменение: ${date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })}`;
};

const compareTreeNodesByName = (a: TreeNode, b: TreeNode) =>
  a.label.localeCompare(b.label, "ru-RU", {
    numeric: true,
    sensitivity: "base",
  });

const compareTreeNodes = (a: TreeNode, b: TreeNode, sortMode: TreeSortMode) => {
  if (sortMode === "dateTimeLastChange") {
    const aTimestamp = getNodeLastChangeTimestamp(a);
    const bTimestamp = getNodeLastChangeTimestamp(b);

    if (
      aTimestamp !== null &&
      bTimestamp !== null &&
      aTimestamp !== bTimestamp
    ) {
      return bTimestamp - aTimestamp;
    }

    if (aTimestamp !== null && bTimestamp === null) return -1;
    if (aTimestamp === null && bTimestamp !== null) return 1;
  }

  return compareTreeNodesByName(a, b);
};

const sortTreeNodes = (nodes: TreeNode[], sortMode: TreeSortMode): TreeNode[] =>
  nodes
    .map((node) => ({
      ...node,
      children: node.children
        ? sortTreeNodes(node.children, sortMode)
        : node.children,
    }))
    .sort((a, b) => compareTreeNodes(a, b, sortMode));

const filterTreeNodes = (
  nodes: TreeNode[],
  searchValue: string
): TreeNode[] => {
  const normalizedSearchValue = normalizeSearchText(searchValue);
  if (!normalizedSearchValue) return nodes;

  return nodes.reduce<TreeNode[]>((acc, node) => {
    const isMatched = normalizeSearchText(node.label).includes(
      normalizedSearchValue
    );
    const filteredChildren = node.children
      ? filterTreeNodes(node.children, normalizedSearchValue)
      : [];

    if (isMatched) {
      acc.push(node);
    } else if (filteredChildren.length > 0) {
      acc.push({
        ...node,
        children: filteredChildren,
      });
    }

    return acc;
  }, []);
};

const getExpandedKeysForSearch = (nodes: TreeNode[]) => {
  const keys: Record<string, boolean> = {};

  const traverse = (items: TreeNode[]) => {
    items.forEach((node) => {
      if (node.children?.length) {
        keys[node.key] = true;
        traverse(node.children);
      }
    });
  };

  traverse(nodes);
  return keys;
};

const TreeView: React.FC<TreeViewProps> = ({
  wells,
  wellbores,
  logs,
  isDataLoading = false,
  fetchWellbores,
  fetchLogs,
  onAddWell,
  onAddWellbore,
  onAddLog,
  onEditNode,
  onDeleteNode,
  onDeleteLog,
  onViewWellbore,
  onToggleObserve,
  isObserved,
  isLogActive,
  selectedWellbore,
  treeControls = true,
  isContextMenu = true,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [loadingKeys, setLoadingKeys] = useState<Record<string, boolean>>({});

  const [wellboresCache, setWellboresCache] = useState<
    Record<string, Wellbore[]>
  >({});
  const [logsCache, setLogsCache] = useState<Record<string, Log[]>>({});

  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [dialogTitle, setDialogTitle] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [sortMode, setSortMode] = useState<TreeSortMode>("name");
  const [targetNode, setTargetNode] = useState<TreeNode | null>(null);

  const pendingWellUidsRef = useRef<Set<string>>(new Set());
  const pendingWellboreUidsRef = useRef<Set<string>>(new Set());
  const cm = useRef<ContextMenu>(null);
  const sortMenu = useRef<Menu>(null);
  const toast = useRef<Toast>(null);

  const buildTreeData = useCallback(() => {
    return wells.map((well) => {
      const wellNode: TreeNode = {
        key: `well-${well.uid}`,
        label: well.name,
        data: well,
        type: "well" as const,
        icon: "pi pi-building",
        lazy: true,
        children: [],
        leaf: false,
      };

      const wellWellbores = wellboresCache[well.uid] || [];
      if (wellWellbores.length > 0) {
        wellNode.lazy = false;
        wellNode.children = wellWellbores.map((wb) => {
          const wbNode: TreeNode = {
            key: `wellbore-${wb.uid}`,
            label: wb.name,
            data: { ...wb, wellUid: well.uid },
            type: "wellbore" as const,
            icon: "pi pi-file",
            lazy: true,
            children: [],
            leaf: false,
          };
          const wbLogs = logsCache[wb.uid] || [];
          if (wbLogs.length > 0) {
            wbNode.lazy = false;
            wbNode.children = wbLogs.map((log) => {
              const isActive = isLogActive?.(log) ?? Boolean(log.objectGrowing);
              return {
                key: `log-${log.uid}`,
                label: getChartTitleTranslation(log.name),
                data: log,
                type: "log" as const,
                icon: isActive ? "pi pi-circle-fill" : "pi pi-circle",
                children: [],
                leaf: true,
              };
            });
          }
          return wbNode;
        });
      }

      return wellNode;
    });
  }, [wells, wellboresCache, logsCache, isLogActive]);

  const treeData = useMemo(
    () => sortTreeNodes(buildTreeData(), sortMode),
    [buildTreeData, sortMode]
  );
  const visibleTreeData = useMemo(
    () => filterTreeNodes(treeData, searchValue),
    [treeData, searchValue]
  );
  const searchExpandedKeys = useMemo(
    () => getExpandedKeysForSearch(visibleTreeData),
    [visibleTreeData]
  );
  const activeExpandedKeys = searchValue.trim()
    ? searchExpandedKeys
    : expandedKeys;

  const handleFetchWellbores = useCallback(
    (wellUid: string) => {
      if (wellboresCache[wellUid]) return;
      const key = `well-${wellUid}`;
      if (loadingKeys[key]) return;

      pendingWellUidsRef.current.add(wellUid);
      setLoadingKeys((prev) => ({ ...prev, [key]: true }));
      fetchWellbores(wellUid);
    },
    [wellboresCache, fetchWellbores, loadingKeys]
  );

  const handleFetchLogs = useCallback(
    (wellUid: string, wellboreUid: string) => {
      if (logsCache[wellboreUid]) return;
      const key = `wellbore-${wellboreUid}`;
      if (loadingKeys[key]) return;

      pendingWellboreUidsRef.current.add(wellboreUid);
      setLoadingKeys((prev) => ({ ...prev, [key]: true }));
      fetchLogs(wellUid, wellboreUid);
    },
    [logsCache, fetchLogs, loadingKeys]
  );

  const prevSelectedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedWellbore) {
      setSelectedKey(null);
      return;
    }

    const { uid, wellUid } = selectedWellbore;
    if (!uid || !wellUid) return;

    const wellboreKey = `wellbore-${uid}`;
    const wellKey = `well-${wellUid}`;
    const newKey = `${wellboreKey}|${wellKey}`;

    if (prevSelectedKeyRef.current === newKey) {
      return;
    }
    prevSelectedKeyRef.current = newKey;

    setSelectedKey(wellboreKey);

    if (!wellboresCache[wellUid] && !loadingKeys[wellKey]) {
      pendingWellUidsRef.current.add(wellUid);
      setLoadingKeys((prev) => ({ ...prev, [wellKey]: true }));
      fetchWellbores(wellUid);
    }

    setExpandedKeys((prev) => ({
      ...prev,
      [wellKey]: true,
    }));
  }, [selectedWellbore, wellboresCache, loadingKeys, fetchWellbores]);

  useEffect(() => {
    if (!wellbores) return;

    let targetUid: string | undefined;
    if (wellbores.length > 0) {
      targetUid = wellbores[0].wellUid;
    }

    const pendingSet = pendingWellUidsRef.current;
    let resolvedUid: string | null = null;

    if (targetUid && pendingSet.has(targetUid)) {
      resolvedUid = targetUid;
    } else if (pendingSet.size === 1) {
      resolvedUid = Array.from(pendingSet)[0];
    } else if (targetUid) {
      resolvedUid = targetUid;
    }

    if (!resolvedUid) return;

    const key = `well-${resolvedUid}`;
    setWellboresCache((prev) => ({ ...prev, [resolvedUid!]: wellbores }));
    setLoadingKeys((prev) => ({ ...prev, [key]: false }));
    pendingSet.delete(resolvedUid);
  }, [wellbores]);

  useEffect(() => {
    if (!logs) return;

    let targetUid: string | undefined;
    if (logs.length > 0) {
      targetUid = logs[0].wellboreUid;
    }

    const pendingSet = pendingWellboreUidsRef.current;
    let resolvedUid: string | null = null;

    if (targetUid && pendingSet.has(targetUid)) {
      resolvedUid = targetUid;
    } else if (pendingSet.size === 1) {
      resolvedUid = Array.from(pendingSet)[0];
    } else if (targetUid) {
      resolvedUid = targetUid;
    }

    if (!resolvedUid) return;

    const key = `wellbore-${resolvedUid}`;
    setLogsCache((prev) => ({ ...prev, [resolvedUid!]: logs }));
    setLoadingKeys((prev) => ({ ...prev, [key]: false }));
    pendingSet.delete(resolvedUid);
  }, [logs]);

  const handleToggle = useCallback((e: { value: Record<string, boolean> }) => {
    const newKeys = e.value;
    setExpandedKeys((prev) => {
      const prevKeys = Object.keys(prev).sort().join(",");
      const nextKeys = Object.keys(newKeys).sort().join(",");
      return prevKeys === nextKeys ? prev : newKeys;
    });
  }, []);

  const handleNodeExpand = useCallback(
    (node: TreeNode) => {
      if (!node.lazy || (node.children && node.children.length > 0)) return;
      if (loadingKeys[node.key]) return;
      if (node.type === "well") {
        if (!wellboresCache[node.data.uid]) {
          handleFetchWellbores(node.data.uid);
        }
      } else if (node.type === "wellbore") {
        if (!logsCache[node.data.uid]) {
          handleFetchLogs(node.data.wellUid, node.data.uid);
        }
      }
    },
    [
      loadingKeys,
      wellboresCache,
      logsCache,
      handleFetchWellbores,
      handleFetchLogs,
    ]
  );

  const handleExpandAll = useCallback(() => {
    const keys: Record<string, boolean> = {};
    const traverse = (nodes: TreeNode[]) => {
      nodes.forEach((node) => {
        if (node.children?.length || node.lazy) {
          keys[node.key] = true;
          if (node.lazy && (!node.children || node.children.length === 0)) {
            if (!loadingKeys[node.key]) {
              if (node.type === "well" && !wellboresCache[node.data.uid]) {
                handleFetchWellbores(node.data.uid);
              } else if (
                node.type === "wellbore" &&
                !logsCache[node.data.uid]
              ) {
                handleFetchLogs(node.data.wellUid, node.data.uid);
              }
            }
          }
          if (node.children) traverse(node.children);
        }
      });
    };
    traverse(treeData);
    setExpandedKeys(keys);
  }, [
    treeData,
    loadingKeys,
    wellboresCache,
    logsCache,
    handleFetchWellbores,
    handleFetchLogs,
  ]);

  const handleCollapseAll = useCallback(() => {
    setExpandedKeys({});
  }, []);

  const handleNodeSelect = useCallback(
    (e: TreeEventNodeEvent) => {
      const node = e.node as TreeNode;

      if (node.type === "wellbore") {
        setSelectedKey(node.key);
        if (onViewWellbore) onViewWellbore(node.data as Wellbore);
      } else if (node.type === "log") {
        // setSelectedKey(null);
        setSelectedNode(node);
        const hasItems = !!(onToggleObserve || onDeleteLog);
        if (hasItems && cm.current) {
          cm.current.show(e.originalEvent as any);
        }
      }
    },
    [onViewWellbore, onToggleObserve, onDeleteLog]
  );

  const openAddDialog = (
    parent: TreeNode,
    type: "well" | "wellbore" | "log"
  ) => {
    setTargetNode(parent);
    setDialogMode("add");
    setDialogTitle(
      `Добавить ${
        type === "well" ? "скважину" : type === "wellbore" ? "секцию" : "лог"
      }`
    );
    setInputValue("");
    setDialogVisible(true);
  };

  const openEditDialog = (node: TreeNode) => {
    setTargetNode(null);
    setDialogMode("edit");
    setDialogTitle("Редактировать");
    setInputValue(node.label);
    setDialogVisible(true);
  };

  const handleSave = () => {
    if (!inputValue.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Внимание",
        detail: "Имя не может быть пустым",
      });
      return;
    }

    if (dialogMode === "edit" && selectedNode) {
      onEditNode?.(selectedNode, inputValue.trim());
    } else if (dialogMode === "add" && targetNode) {
      const parent = targetNode;
      if (parent.type === "well") {
        onAddWellbore?.(parent.data.uid, inputValue.trim());
      } else if (parent.type === "wellbore") {
        onAddLog?.(parent.data.wellUid, parent.data.uid, inputValue.trim());
      }
    }
    setDialogVisible(false);
  };

  const handleDeleteLog = useCallback(
    async (log: Log) => {
      if (!onDeleteLog) return;
      if (!window.confirm(`Удалить лог "${log.name}"?`)) return;

      const { success, error } = await onDeleteLog(log);
      if (!success) {
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: error ?? "Не удалось удалить лог",
        });
        return;
      }

      setLogsCache((prev) => {
        const wellboreLogs = prev[log.wellboreUid];
        if (!wellboreLogs) return prev;
        return {
          ...prev,
          [log.wellboreUid]: wellboreLogs.filter(
            (item) => item.uid !== log.uid
          ),
        };
      });
      setSelectedKey((prev) => (prev === `log-${log.uid}` ? null : prev));

      toast.current?.show({
        severity: "success",
        summary: "Успешно",
        detail: "Лог удален",
      });
    },
    [onDeleteLog]
  );

  const menuItems = useMemo(() => {
    if (!selectedNode) return [];

    const items = [];

    if (selectedNode.type === "wellbore" && onViewWellbore) {
      items.push({
        label: "Просмотр",
        icon: "pi pi-eye",
        command: () => {
          setSelectedKey(selectedNode.key);
          onViewWellbore(selectedNode.data as Wellbore);
        },
      });
    }

    if (selectedNode.type === "log" && onToggleObserve) {
      const log = selectedNode.data as Log;
      const observed = isObserved?.(log.uid) || false;
      items.push({
        label: observed ? "Завершить отслеживание" : "Отслеживать",
        icon: observed ? "pi pi-stop-circle" : "pi pi-play",
        command: () => {
          onToggleObserve(log.wellUid, log.wellboreUid, log.uid);
        },
      });
    }

    if (selectedNode.type === "log" && onDeleteLog) {
      const log = selectedNode.data as Log;
      items.push({
        label: "Удалить лог",
        icon: "pi pi-trash",
        command: () => handleDeleteLog(log),
      });
    }

    if (isContextMenu) {
      if (selectedNode.type === "well") {
        items.push({
          label: "Добавить секцию",
          icon: "pi pi-plus",
          command: () => openAddDialog(selectedNode, "wellbore"),
        });
      } else if (selectedNode.type === "wellbore") {
        items.push({
          label: "Добавить лог",
          icon: "pi pi-plus",
          command: () => openAddDialog(selectedNode, "log"),
        });
      }

      items.push({
        label: "Редактировать",
        icon: "pi pi-pencil",
        command: () => openEditDialog(selectedNode),
      });

      if (!(selectedNode.type === "log" && onDeleteLog)) {
        items.push({
          label: "Удалить",
          icon: "pi pi-trash",
          command: () => {
            if (window.confirm(`Удалить "${selectedNode.label}"?`)) {
              onDeleteNode?.(selectedNode);
            }
          },
        });
      }
    }

    return items;
  }, [
    selectedNode,
    isContextMenu,
    onViewWellbore,
    onToggleObserve,
    isObserved,
    onAddWellbore,
    onAddLog,
    onEditNode,
    onDeleteNode,
    onDeleteLog,
    handleDeleteLog,
  ]);

  const handleContextMenu = useCallback(
    (e: TreeEventNodeEvent | any) => {
      const node = e.node as TreeNode;
      setSelectedNode(node);

      const hasItems =
        (node.type === "wellbore" && onViewWellbore) ||
        (node.type === "log" && (onToggleObserve || onDeleteLog)) ||
        (isContextMenu && (node.type === "well" || node.type === "wellbore"));

      if (hasItems) {
        cm.current?.show(e.originalEvent.nativeEvent as any);
      }
    },
    [isContextMenu, onViewWellbore, onToggleObserve, onDeleteLog]
  );

  const sortMenuItems = useMemo(
    () => [
      {
        label: "По имени",
        icon: sortMode === "name" ? "pi pi-check" : "pi pi-sort-alpha-down",
        command: () => setSortMode("name"),
      },
      {
        label: "По времени изменения",
        icon: sortMode === "dateTimeLastChange" ? "pi pi-check" : "pi pi-clock",
        command: () => setSortMode("dateTimeLastChange"),
      },
    ],
    [sortMode]
  );

  const nodeTemplate = (node: any) => {
    const isLoading = loadingKeys[node.key];
    const isActiveWellbore =
      node.type === "wellbore" && Boolean(node.data.isActive);
    const isActiveWell = node.type === "well" && Boolean(node.data.isActive);
    const isObservedLog = node.type === "log" && isObserved?.(node.data.uid);
    const lastChangeTooltip = formatLastChangeTooltip(node as TreeNode);

    return (
      <NodeContainer title={lastChangeTooltip}>
        <span>{node.label}</span>
        {isLoading && (
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "0.8rem" }} />
        )}
        {isObservedLog && (
          <i
            className="pi pi-eye"
            style={{ fontSize: "1rem", position: "absolute", right: "17px" }}
          />
        )}
        {isActiveWell && (
          <i
            className="pi pi-spin pi-sync"
            style={{
              fontSize: "0.8rem",
              color: "#4CAF50",
              position: "absolute",
              right: "17px",
            }}
          />
        )}
        {isActiveWellbore && (
          <i
            className="pi pi-spin pi-sync"
            style={{
              fontSize: "0.8rem",
              color: "#4CAF50",
              position: "absolute",
              right: "17px",
            }}
          />
        )}
      </NodeContainer>
    );
  };

  return (
    <TreeContainer>
      <TreeBody>
        <Toast ref={toast} position="bottom-center" />
        <StyledContextMenu model={menuItems} ref={cm} />
        <Menu model={sortMenuItems} popup ref={sortMenu} />
        <TreeSearchBar>
          <span className="p-input-icon-left">
            <InputText
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Поиск"
              className="tree-search-input p-inputtext-sm"
            />
            <i className="pi pi-search" />
          </span>
          <Button
            icon="pi pi-filter"
            className="tree-sort-button"
            severity="secondary"
            text
            aria-label="Сортировка дерева"
            title="Сортировка дерева"
            onClick={(event) => sortMenu.current?.toggle(event)}
          />
        </TreeSearchBar>
        <Tree
          value={visibleTreeData}
          selectionMode="single"
          selectionKeys={selectedKey}
          onSelect={handleNodeSelect}
          expandedKeys={activeExpandedKeys}
          onToggle={handleToggle}
          onExpand={(e) => handleNodeExpand(e.node as TreeNode)}
          onContextMenu={handleContextMenu}
          emptyMessage={isDataLoading ? "Загрузка..." : "Нет данных"}
          nodeTemplate={nodeTemplate}
        />
      </TreeBody>

      {treeControls && (
        <TreeControls>
          <Button
            icon="pi pi-plus"
            size="small"
            outlined
            title="Добавить скважину"
            onClick={() => {
              setTargetNode(null);
              setDialogMode("add");
              setDialogTitle("Добавить скважину");
              setInputValue("");
              setDialogVisible(true);
            }}
          />
          <Button
            icon={
              Object.keys(expandedKeys).length
                ? "pi pi-angle-double-up"
                : "pi pi-angle-double-down"
            }
            size="small"
            title={
              Object.keys(expandedKeys).length
                ? "Свернуть все"
                : "Развернуть все"
            }
            onClick={
              Object.keys(expandedKeys).length
                ? handleCollapseAll
                : handleExpandAll
            }
          />
        </TreeControls>
      )}

      <Dialog
        header={dialogTitle}
        visible={dialogVisible}
        style={{ width: "350px" }}
        onHide={() => setDialogVisible(false)}
        footer={
          <div>
            <Button
              label="Отмена"
              icon="pi pi-times"
              outlined
              onClick={() => setDialogVisible(false)}
            />
            <Button label="Сохранить" icon="pi pi-check" onClick={handleSave} />
          </div>
        }
      >
        <div className="p-field">
          <label htmlFor="nodeName">Имя</label>
          <InputText
            id="nodeName"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="p-inputtext-sm"
            style={{ width: "100%" }}
          />
        </div>
      </Dialog>
    </TreeContainer>
  );
};

export default TreeView;
